import { _ } from "../../core/polyglot/polyglot.js";
import { makeMonitorRepository } from "../../core/repositories/repositoryBuilders.js";
import FiscalYear from "../../models/period/FiscalYear.js";
import notifier from "../../core/widgets/notifier/notifier.js";
import session from "../../core/session.js";

/**
 * `BudgetImporter` is a Facade responsible for handling the upload of an SIE file, its
 * validation and the import of the budget (once a budget name is chosen).
 *
 * This is performed in two steps:
 * - The `saveBudgetInfo` method saves the uploaded file and pass the result of the
 *   upload to its callback function as a `BudgetUploadResult`.
 * - Once the budget name has been chosen, calling `importBudget` saves the new budget.
 */
export default class BudgetImporter {
	constructor() {
		const antiForgeryToken = session.getAntiForgeryToken();

		this._budgetInfoRepository = makeMonitorRepository({
			route: "/Shared/Import/Budget/Sie",
			antiForgeryToken,
		});

		this._importBudgetRepository = makeMonitorRepository({
			route: "/Shared/Company/#companyId/Budget/Sie",
			defaultFilters: {
				companyId: session.getCompany().getLegacyId(),
			},
			antiForgeryToken,
		});
	}

	getUploadUrl() {
		return "/Shared/Import/Upload/Budget/Sie";
	}

	saveBudgetInfo() {
		const json = {
			agencyId: session.getAgency() ? session.getAgency().getId() : null,
			companyId: session.getCompany().getLegacyId(),
		};

		return this._budgetInfoRepository
			.save({ model: json, successMessage: null })
			.then(({ errors, importCompanyInfo }) => {
				if (this._hasUploadErrors(errors)) {
					return null;
				}

				return this._makeFiscalYear(importCompanyInfo);
			});
	}

	importBudget(budgetName) {
		return this._importBudgetRepository.save({
			model: budgetName,
			successMessage: null,
			errorMessage: _("Failed to import data"),
		});
	}

	_makeFiscalYear(importCompanyInfo) {
		const fiscalYearData =
			this._getUniqueFiscalYearFromSie(importCompanyInfo);
		return FiscalYear.materialize(fiscalYearData);
	}

	_getUniqueFiscalYearFromSie(importCompanyInfo) {
		return importCompanyInfo.fiscalYears[0];
	}

	_hasUploadErrors(errors) {
		if (!errors.length) {
			return false;
		}

		errors.forEach((error) =>
			notifier.error(`${error.title}. ${error.explanation}`),
		);

		return true;
	}
}
